import React from 'react';
import { AlertCircle, CheckCircle2, X, Loader2 } from 'lucide-react';
import { useCartAssistant, useCartAssistantInput } from "../../../lib/cartAssistant/CartAssistantProvider";
import { useTranslation } from 'react-i18next';
import { getConfirmationStatus, setConfirmationStatus } from '../../../lib/confirmations';
import { SmartImage } from '../../../../components/common/SmartImage';
import { UnitPriceIconButton, UnitPriceCheckDetail, applyLooksRight } from '../../../../components/common/UnitPriceCheckPanel';
import { liveState } from '../../../lib/store';

import { ShoppingItemSchema, ShoppingItem } from '../../../lib/schemas';

interface ConfirmActionCardProps {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'info' | 'success';
  action?: string;
  nonce: string; 
  items?: Array<Partial<ShoppingItem>>; // Use standardized type
  currencySymbol?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export const ConfirmActionCard: React.FC<{ props: ConfirmActionCardProps }> = ({ 
  props
}) => {
  const { t } = useTranslation();
  const { 
    message, 
    title = t('chat.confirm_action'),
    confirmLabel = t('common.add'),
    cancelLabel = t('common.cancel'),
    variant = 'info',
    action,
    nonce, 
    items,
    currencySymbol = '$', // Default to $
    onConfirm,
    onCancel
  } = props || {};
  
  // Get CartAssistant context
  const assistantInput = useCartAssistantInput();
  const { messages, currentThreadId, thread, cancelRun, isIdle } = useCartAssistant();
  
  // Initialize state from consolidated localStorage object on mount
  const [confirmed, setConfirmed] = React.useState(() => getConfirmationStatus(nonce) === 'confirmed');
  const [cancelled, setCancelled] = React.useState(() => getConfirmationStatus(nonce) === 'cancelled');
  const [isPending, setIsPending] = React.useState(false);
  // Item whose shelf unit-price detail is open.
  const [openCheck, setOpenCheck] = React.useState<number | null>(null);
  // The staged extraction behind each card item, found once by name: "Looks
  // right" renames it (the size is in the name), and add_all_items_to_basket
  // adds the staged items, so the fix lands in the basket.
  const stagedRef = React.useRef<Record<number, any>>({});
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);

  // RESILIENCE: Check if thread is stuck waiting for tool calls
  const isStuck = React.useMemo(() => {
    const threadAny = thread as any;
    return threadAny?.thread?.status === 'requires_action' || 
           (threadAny?.pendingToolCallIds && threadAny.pendingToolCallIds.length > 0) ||
           (!isIdle && !threadAny?.streaming?.isActive);
  }, [thread, isIdle]);

  // RESET LOGIC: If nonce changes, reset local state
  // This handles cases where React reuses the component instance
  React.useEffect(() => {
    setConfirmed(getConfirmationStatus(nonce) === 'confirmed');
    setCancelled(getConfirmationStatus(nonce) === 'cancelled');
    setIsPending(false);
  }, [nonce]);
  
  // Track programmatic submission
  const [waitingToSubmit, setWaitingToSubmit] = React.useState<string | null>(null);

  // Always-current ref so async retries get the latest submit/setValue, not a stale closure
  const assistantInputRef = React.useRef(assistantInput);
  React.useLayoutEffect(() => {
    assistantInputRef.current = assistantInput;
  });

  // PERSISTENCE CHECK: Look through messages to see if this has already been approved
  React.useEffect(() => {
    // Only check messages if local storage hasn't already determined state
    if (confirmed || cancelled) return;

    const approvalString = `[TERMINAL_USER_APPROVAL]:${nonce}:${action || 'proceed'}`;
    const cancellationString = `[TERMINAL_USER_CANCELLED]:${nonce}:${action || 'cancel'}`;

    const isAlreadyApprovedInMessages = messages.some(msg => {
      if (typeof (msg as any).content === 'string') return (msg as any).content.includes(approvalString);
      if (Array.isArray((msg as any).content)) {
        return (msg as any).content.some((block: any) => block.type === 'text' && block.text?.includes(approvalString));
      }
      return false;
    });

    const isAlreadyCancelledInMessages = messages.some(msg => {
      if (typeof (msg as any).content === 'string') return (msg as any).content.includes(cancellationString);
      if (Array.isArray((msg as any).content)) {
        return (msg as any).content.some((block: any) => block.text?.includes(cancellationString));
      }
      return false;
    });

    if (isAlreadyApprovedInMessages) {
      setConfirmed(true);
      setConfirmationStatus(nonce, 'confirmed', currentThreadId);
    } else if (isAlreadyCancelledInMessages) {
      setCancelled(true);
      setConfirmationStatus(nonce, 'cancelled', currentThreadId);
    }
  }, [messages, nonce, action, confirmed, cancelled, currentThreadId]);

  // SYNC SUBMISSION LOGIC: Wait for value to be set then fire submit
  React.useEffect(() => {
    const fireSubmit = async (isRetry = false) => {
      if (!waitingToSubmit) return;
      
      // We check the REF instead of the closure value to ensure we have the absolute latest state
      const currentInput = assistantInputRef.current;
      
      if (currentInput.value === waitingToSubmit && !currentInput.isDisabled) {
        try {
          // PROACTIVE RECOVERY: If the thread is stuck waiting for tool calls from a previous run,
          // submit() will throw 400. We cancel the existing run here to ensure success.
          if (isStuck && !isRetry) {
            console.warn("[CartAssistant] [ConfirmActionCard] Thread state is stuck. Attempting to cancel run...");
            if (cancelRun) {
              try {
                await cancelRun();
                console.log("[CartAssistant] [ConfirmActionCard] Stuck run cancelled proactively.");
                await new Promise(resolve => setTimeout(resolve, 1500));
                // Re-sync the value after cancel
                currentInput.setValue(waitingToSubmit);
                await new Promise(resolve => setTimeout(resolve, 500));
              } catch (err) {
                console.error("[CartAssistant] [ConfirmActionCard] Proactive cancel failed:", err);
              }
            }
          }

          console.log(`[CartAssistant] [ConfirmActionCard] API SUBMIT INITIATED (isRetry: ${isRetry})`, {
            threadId: currentThreadId,
            nonce
          });

          await currentInput.submit();
          setWaitingToSubmit(null);
          
          // If we reached here, it succeeded
          if (waitingToSubmit.includes('[TERMINAL_USER_APPROVAL]')) {
            setConfirmed(true);
            setConfirmationStatus(nonce, 'confirmed', currentThreadId);
          }
        } catch (err: any) {
          const errorMsg = err?.message || String(err);
          const is400 = errorMsg.includes('400') || 
                       errorMsg.includes('MISSING_RESULTS') || 
                       err?.status === 400 ||
                       err?.code === 'MISSING_RESULTS';

          console.error(`[CartAssistant] [ConfirmActionCard] Submission failed (isRetry: ${isRetry}):`, errorMsg);

          const isInvalidPreviousRun = errorMsg.includes('invalid_previous_run') ||
                                       errorMsg.includes('Invalid Previous Run');

          if (is400 && !isRetry) {
            if (isInvalidPreviousRun) {
              // The SDK's cached previousRunId is stale — the server has advanced the thread.
              // Calling submit() again on assistantInputRef won't help because the component hasn't
              // re-rendered since the failure, so the ref still holds the same stale closure.
              // Reset state to null and re-set via setTimeout so the effect fires fresh on the
              // next render cycle, where assistantInputRef will have the updated SDK state.
              console.warn("[CartAssistant] [ConfirmActionCard] invalid_previous_run detected. Resetting state to retrigger fresh effect cycle...");
              const resubmitValue = waitingToSubmit;
              setWaitingToSubmit(null);
              setTimeout(() => setWaitingToSubmit(resubmitValue), 3000);
            } else {
              // Handle other 400s (like MISSING_RESULTS)
              if (!isIdle) {
                try {
                  if (cancelRun) {
                    await cancelRun();
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    assistantInputRef.current.setValue(waitingToSubmit);
                    await new Promise(resolve => setTimeout(resolve, 500));
                    return await fireSubmit(true);
                  }
                } catch (recoveryErr) {
                  console.error("[CartAssistant] [ConfirmActionCard] Recovery retry failed:", recoveryErr);
                }
              }
              setWaitingToSubmit(null);
            }
          } else {
            setWaitingToSubmit(null);
          }
        }
      }
    };

    fireSubmit();
  }, [waitingToSubmit, assistantInput.value, assistantInput.isDisabled, nonce, currentThreadId, cancelRun, isStuck, isIdle]);

  const handleConfirm = async () => {
    if (onConfirm) onConfirm();
    
    // STATE INJECTION: Include full items data in the message history.
    // This prevents the AI from "forgetting" prices/categories after the confirmation step.
    const itemsData = items && items.length > 0 
      ? ` [ITEMS_JSON:${JSON.stringify(items)}]` 
      : '';
    const itemsSummary = items && items.length > 0 
      ? ` (Confirmed: ${items.map(i => i.name).join(', ')})` 
      : '';
      
    const confirmMsg = `[TERMINAL_USER_APPROVAL]:${nonce}:${action || 'proceed'}${itemsSummary}${itemsData}`;
    
    if (!assistantInput.isDisabled) {
      assistantInput.setValue(confirmMsg);
      setWaitingToSubmit(confirmMsg);
      setIsPending(false);
    } else {
      // Input is busy, queue it via existing pending mechanism
      setIsPending(true);
    }
  };

  // QUEUE LOGIC: If we were pending and input becomes free, fire!
  React.useEffect(() => {
    if (isPending && !assistantInput.isDisabled && !confirmed && !cancelled) {
      handleConfirm();
    }
  }, [assistantInput.isDisabled, isPending, confirmed, cancelled]);

  const handleCancel = async () => {
    setCancelled(true);
    setConfirmationStatus(nonce, 'cancelled', currentThreadId);
    if (onCancel) onCancel();
    
    const cancelMsg = `[TERMINAL_USER_CANCELLED]:${nonce}:${action || 'cancel'}`;
    if (!assistantInput.isDisabled) {
      assistantInput.setValue(cancelMsg);
      setWaitingToSubmit(cancelMsg);
    }
  };

  if (confirmed) {
    return (
      <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100 flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-300 shadow-sm">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3 text-emerald-600 shadow-inner">
          <CheckCircle2 size={24} />
        </div>
        <h4 className="text-xs font-black text-emerald-800 uppercase tracking-widest leading-none mb-1">{t('chat.action_confirmed')}</h4>
        <p className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-tighter">{t('chat.basket_updated')}</p>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-300 opacity-60 shadow-sm">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 text-gray-400">
          <X size={24} />
        </div>
        <h4 className="text-xs font-black text-gray-800 uppercase tracking-widest leading-none mb-1">{t('chat.action_cancelled')}</h4>
        <p className="text-[10px] font-bold text-gray-400/70 uppercase tracking-tighter">{t('chat.no_changes_made')}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300 p-6`}>
      <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-xl shrink-0 ${variant === 'danger' ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-500'} shadow-sm border border-gray-50`}>
          <AlertCircle size={20} />
        </div>
        <div className="min-w-0 flex-1">
           <h4 className={`text-xs font-black uppercase tracking-widest leading-none mb-1 ${variant === 'danger' ? 'text-rose-600' : 'text-[#1a2b3c]'}`}>{title}</h4>
           
           {items && items.length > 0 ? (
             <div className="mt-3 space-y-2">
               {items.map((item, i) => {
                 // Items not in the basket yet: the check comes from the staged extraction.
                 if (!stagedRef.current[i]) {
                   stagedRef.current[i] = [...(liveState.pendingExtractedItems || []), ...(liveState.pendingReceiptItems || [])].find((p: any) => p.name === item.name)
                     || (item.metadata?.unit_price_check ? item : undefined);
                 }
                 const staged = stagedRef.current[i] as ShoppingItem | undefined;
                 const looksRight = staged ? () => {
                   applyLooksRight(staged, (_id, updates) => { Object.assign(staged, updates); rerender(); });
                   setOpenCheck(null);
                 } : undefined;
                 return (
                 <div key={i} className="bg-gray-50/50 p-2.5 rounded-xl border border-gray-100/50">
                 <div className="flex items-start justify-between gap-3">
                   <div className="flex items-start gap-3 flex-1 min-w-0">
                     {item.imageUrl && (
                       <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 shrink-0 bg-white">
                         <SmartImage 
                           imageUrl={item.imageUrl} 
                           alt={item.name || ''} 
                           className="w-full h-full object-cover" 
                         />
                       </div>
                     )}
                     <div className="flex-1 min-w-0 py-0.5">
                       <p className="text-[11px] font-bold text-[#1a2b3c] leading-snug break-words">{staged?.name || item.name}</p>
                       {item.quantity && item.quantity > 1 && (
                         <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter mt-0.5">{t('common.quantity')}: {item.quantity}</p>
                       )}
                     </div>
                   </div>
                   {item.price != null && (
                     <div className="flex flex-col items-end gap-1 shrink-0">
                        <p className="text-[11px] font-black text-emerald-600 bg-emerald-50/50 px-2 py-1 rounded-lg border border-emerald-100/20">
                          {currencySymbol}{item.price.toFixed(2)}
                        </p>
                        {staged && <UnitPriceIconButton item={staged} isExpanded={openCheck === i} onToggle={() => setOpenCheck(openCheck === i ? null : i)} />}
                     </div>
                   )}
                 </div>
                 {staged && <UnitPriceCheckDetail item={staged} isExpanded={openCheck === i} currencySymbol={currencySymbol} onLooksRight={looksRight} />}
                 </div>
                 );
               })}
             </div>
           ) : (
             <p className="text-[12px] font-bold text-gray-500 leading-tight mt-2 whitespace-pre-wrap">{message}</p>
           )}
        </div>
      </div>

      <div className="flex gap-3">
        <button 
          onClick={handleCancel}
          className="flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-400 border border-gray-100 hover:bg-gray-50 transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          <X size={12} /> {cancelLabel}
        </button>
        <button 
          onClick={handleConfirm}
          className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${variant === 'danger' ? 'bg-rose-500 shadow-rose-100 hover:bg-rose-600' : 'bg-emerald-500 shadow-emerald-100 hover:bg-emerald-600'}`}
        >
          {isPending ? (
            <>
              <Loader2 size={12} className="animate-spin" /> {t('common.loading')}
            </>
          ) : (
            <>
              <CheckCircle2 size={12} /> {confirmLabel}
            </>
          )}
        </button>
      </div>
    </div>
  );
};
